import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

function Select({ options, selected, changeHandler, disabled, placeholder, className, enableFilter, dropdownStyle }) {
  const [filter, setFilter] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const [inModal, setInModal] = useState(false);

  useEffect(() => {
    setInModal(!!containerRef.current?.closest('.modal-body, .confirmation-popup'));
  }, []);

  useEffect(() => {
    if (isOpen && inModal) {
      containerRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen, inModal]);

  const handleOptionSelect = (value) => {
    if (changeHandler) {
      changeHandler(value);
    }
    setIsOpen(false);
  };

  const displayedText = selected ? options.find((option) => option.value === selected)?.text : placeholder;
  const filteredOptions = options.filter((option) => option.text.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div ref={containerRef} className={`select-container ${className} position-relative`}>
      <div
        className={`form-control d-flex justify-content-between align-items-center`}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        role="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen((prev) => !prev);
          }
        }}
        style={{ cursor: 'pointer' }}>
        <span>{displayedText}</span>
        <span className="ml-auto">&#9662;</span>
      </div>

      {isOpen && (
        <div
          className={`dropdown-menu show`}
          style={{
            // Inside a modal an absolutely positioned dropdown gets clipped by the scrollable
            // modal body, so render it in-flow there to let the modal grow with it.
            position: inModal ? 'static' : 'absolute',
            zIndex: 1000,
            width: '100%',
            maxHeight: '200px',
            overflowY: 'auto',
            ...dropdownStyle,
          }}>
          {enableFilter && (
            <input
              type="text"
              placeholder="Filter options..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              disabled={disabled}
              className="form-control mb-2"
            />
          )}
          {filteredOptions.length === 0 ? (
            <div className="dropdown-item text-muted">No options found</div>
          ) : (
            filteredOptions.map((option) => (
              <button key={option.value} onClick={() => handleOptionSelect(option.value)} className="dropdown-item" style={{ padding: '10px 15px' }}>
                {option.text}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

Select.propTypes = {
  options: PropTypes.array.isRequired,
  selected: PropTypes.any,
  changeHandler: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  enableFilter: PropTypes.bool,
  dropdownStyle: PropTypes.object,
};

Select.defaultProps = {
  enableFilter: false,
};

export default memo(Select);
